'use client'

import { useMemo } from 'react'
import { useQuery } from '@apollo/client/react'
import { useAuth } from '@/contexts/AuthContext'
import { useOptionalSelection } from '@/contexts/SelectionContext'
import { usePropertyRole } from './usePropertyRole'
import { useActiveRoleOverride } from './useActiveRoleOverride'
import { deriveActiveHasuraRole } from '@/lib/active-role'
import {
  MyEffectiveCapabilitiesDocument,
  TenantAdminRoleCapabilitiesDocument,
} from '@/graphql/capabilities.generated'
import {
  CAPABILITY_BYPASS_ROLES,
  ROLE_GOVERNED_ADMIN_ROLE,
  decideCapability,
  isCapabilityGatingEnabled,
} from '@/lib/capabilities/gating'
import { FETCH_POLICIES, POLL_INTERVALS } from '@/lib/apollo-policies'

/**
 * The current viewer's effective capability set — wave 6 governance tiers,
 * keyed on the ACTIVE Hasura role (the role the data layer evaluates):
 *
 * - `owner` / legacy `admin` → full bypass, no query. The owner is the
 *   ceiling; the matrix never governs (or locks out) the owners themselves.
 * - `tenant_admin` → ROLE-governed: the set is the tenant's `tenant_admin`
 *   SYSTEM role's rows (public.tenant_admin_effective_capabilities), NOT the
 *   user's membership union — a founder who also holds the owner matrix role
 *   must still see the Tenant Admin role's unticks take effect.
 * - everything else (`staff`) → membership-governed via
 *   public.user_effective_capabilities (unchanged since wave 1): one query
 *   per (user, activeProperty), property rows apply only to the active
 *   property, tenant-wide rows (property_id NULL) apply everywhere.
 *
 * NEXT_PUBLIC_CAPABILITY_GATING !== '1' (default) → everything allowed, no
 * query — behavior-neutral. Flag on → fail-closed per key.
 */
export function useCapabilitySet(): {
  gatingEnabled: boolean
  bypass: boolean
  /** The active Hasura role is `tenant_admin` (its set is the Tenant Admin ROLE's rows). */
  adminGoverned: boolean
  capabilitySet: ReadonlySet<string> | null
  loading: boolean
} {
  const gatingEnabled = isCapabilityGatingEnabled()
  const { user } = useAuth()
  // Optional for the same reason as usePropertyRole: the shared CRUD DataTable
  // reaches this hook from the growth portal, which mounts no SelectionProvider.
  // Both ids null ⇒ the staff query skips, exactly as it does before a property
  // is picked; gating stays fail-closed rather than throwing the page away.
  const selection = useOptionalSelection()
  const selectedTenantId = selection?.selectedTenantId ?? null
  const selectedPropertyId = selection?.selectedPropertyId ?? null
  const { roleKey, loading: roleLoading } = usePropertyRole()
  const overrideRole = useActiveRoleOverride()

  const activeRole = deriveActiveHasuraRole({
    defaultRole: user?.defaultRole ?? null,
    allowedRoles: user?.roles ?? [],
    overrideRole,
    // usePropertyRole returns the override when one is set; the raw property
    // role only matters for the flag-gated auto-pin, where they agree.
    propertyRoleKey: roleKey,
  })

  const bypass = !!activeRole && CAPABILITY_BYPASS_ROLES.has(activeRole)
  const adminGoverned = activeRole === ROLE_GOVERNED_ADMIN_ROLE

  // Hasura rejects `_eq: null`, so the staff query needs a concrete property
  // id — without one (no property selected yet) we skip and stay fail-closed.
  const skipStaff =
    !gatingEnabled || bypass || adminGoverned || !user?.id || !selectedPropertyId
  /*
    TKT-20260915-0002 — `cache-first` KEPT, and a poll ADDED. Both capability
    reads run on EVERY screen (37 files call useCapability, 8 more call the
    batch variant), so `cache-and-network` here would put a background request
    on every navigation of the whole app for a set that changes maybe once a
    month. But `cache-first` with NO poll — what these two were — means the
    first read of a browser tab is the ONLY read that tab ever makes: a
    capability GRANTED or REVOKED by an admin never reached a session already
    open, for as long as it stayed open.

    ⛔ This is the security-adjacent one: the matrix gates what a user may SEE
    and DO, and `decideCapability` fails CLOSED, so the stale direction that
    matters is a REVOKE that the open tab keeps honouring as a grant. The poll
    bounds that window at POLL_INTERVALS.SEMI_STATIC (10 min) instead of
    "never". It is strictly FASTER than today's behaviour in both directions
    and never slower — do not remove the poll without replacing it with
    something at least as prompt.
  */
  const { data, loading } = useQuery(MyEffectiveCapabilitiesDocument, {
    variables: { propertyId: selectedPropertyId },
    skip: skipStaff,
    fetchPolicy: FETCH_POLICIES.SEMI_STATIC,
    pollInterval: POLL_INTERVALS.SEMI_STATIC,
  })

  // Role-governed tenant_admin tier: the role's rows for the selected tenant.
  const skipAdmin = !gatingEnabled || !adminGoverned || !user?.id
  const { data: adminData, loading: adminLoading } = useQuery(
    TenantAdminRoleCapabilitiesDocument,
    // Same reasoning as the staff read above — see the note there.
    {
      skip: skipAdmin,
      fetchPolicy: FETCH_POLICIES.SEMI_STATIC,
      pollInterval: POLL_INTERVALS.SEMI_STATIC,
    }
  )

  const capabilitySet = useMemo<ReadonlySet<string> | null>(() => {
    if (adminGoverned) {
      if (!adminData) return null
      const set = new Set<string>()
      for (const row of adminData.tenant_admin_effective_capabilities) {
        if (selectedTenantId && row.tenant_id !== selectedTenantId) continue
        if (row.capability_key) set.add(row.capability_key)
      }
      return set
    }
    if (!data) return null
    const set = new Set<string>()
    for (const row of data.user_effective_capabilities) {
      // The view returns rows for EVERY tenant the user belongs to — only the
      // selected tenant's rows apply here.
      if (selectedTenantId && row.tenant_id !== selectedTenantId) continue
      if (row.capability_key) set.add(row.capability_key)
    }
    return set
  }, [adminGoverned, adminData, data, selectedTenantId])

  return {
    gatingEnabled,
    bypass,
    adminGoverned,
    capabilitySet,
    loading:
      gatingEnabled &&
      !bypass &&
      (adminGoverned ? adminLoading : roleLoading || loading),
  }
}

/**
 * May the current user use capability `key`? Fail-closed when gating is on
 * (loading or missing key → not allowed); always allowed when gating is off.
 */
export function useCapability(key: string): { allowed: boolean; loading: boolean } {
  const { gatingEnabled, bypass, capabilitySet, loading } = useCapabilitySet()
  return {
    allowed: decideCapability(key, { gatingEnabled, bypass, capabilitySet }),
    loading,
  }
}

/**
 * Batch variant of useCapability — one shared query, a decision per key.
 * Returns a key→allowed map plus the shared loading state.
 */
export function useCapabilities(keys: string[]): {
  allowed: Record<string, boolean>
  loading: boolean
} {
  const { gatingEnabled, bypass, capabilitySet, loading } = useCapabilitySet()
  const allowed = useMemo(() => {
    const out: Record<string, boolean> = {}
    for (const key of keys) {
      out[key] = decideCapability(key, { gatingEnabled, bypass, capabilitySet })
    }
    return out
    // keys is typically an inline array literal — depend on its contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keys.join(' '), gatingEnabled, bypass, capabilitySet])
  return { allowed, loading }
}
